/**
 * Extract plaintext from (sanitized) rich-text HTML — for a compact excerpt like the manual card (ADR-013 §3).
 *
 * SECURITY (plan panel #1): this MUST use an **inert** parse. `DOMParser().parseFromString(html, "text/html")` builds a
 * document that is never attached to the live page, so scripts don't run and resource-loading attributes (`<img
 * onerror>`) never fire — unlike `element.innerHTML = html`, which fires them **even on a detached node** (and jsdom
 * silently passes such a test, hiding the regression). We only ever read `.textContent`; no HTML is rendered.
 *
 * Block-ish tags (`<br>`, `</p>`, `</li>`, headings, `<hr>`) are turned into spaces BEFORE parsing so words on separate
 * lines don't fuse into "line1line2" in the excerpt (legacy descriptions use `<br>` heavily). That is pure string
 * substitution inserting spaces — it renders nothing; the DOMParser still does the actual, inert tag stripping.
 */
export function htmlToText(html: string | null | undefined): string {
  if (!html) return "";
  const cleaned = html
    // Drop comments + non-text blocks first, so their SOURCE (a `<script>`'s body is part of `textContent`) doesn't
    // surface in the excerpt. Cosmetic — the API sanitizes descriptions on read, so scripts never reach a real client;
    // and the DOMParser below is the actual inert security boundary regardless of this regex's completeness.
    .replace(/<!--[\s\S]*?-->/g, " ")
    .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, " ")
    // Block-ish breaks → a space so words on separate lines don't fuse ("line1line2").
    .replace(/<(?:br|hr)\s*\/?>|<\/(?:p|li|h[1-6]|blockquote|ul|ol|div)>/gi, " ");
  if (typeof DOMParser === "undefined") {
    // SSR / no-DOM fallback: naive tag strip (never rendered).
    return cleaned.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  }
  const doc = new DOMParser().parseFromString(cleaned, "text/html");
  return (doc.body.textContent ?? "").replace(/\s+/g, " ").trim();
}
